import React, { useCallback } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { DrawerActions, DrawerView } from '@divvy-web/skylab.drawer'
import { FormTextInput } from '@divvy-web/skylab.form'
import { FormProvider, useForm, useFormState } from '@divvy-web/skylab.react-hook-form'
import { TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import Collaborator from '@sputnik/components/InviteCollaborators/Collaborator'
import { InviteCollaboratorsDrawerProps } from '@sputnik/components/InviteCollaborators/types'
import useApplicationCollaborators from '@sputnik/hooks/useApplicationCollaborators'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import { useInviteUsers } from './InviteUsers.gql'
import styles from './LegacyInviteCollaboratorsDrawerView.module.scss'
import { useInviteCollaboratorsSchema } from './useInviteCollaboratorsSchema'

export const LegacyInviteCollaboratorsDrawerView = ({
  appId,
  isShowing,
  onClose,
  icon,
  onDeleteCollaborator,
  onInviteCollaborator,
}: InviteCollaboratorsDrawerProps) => {
  const showToast = useToast()

  const [getClassName] = useNameFormatter('LegacyInviteCollaboratorsDrawerView')

  const [inviteUsers] = useInviteUsers({
    refetchQueries: ['GetApplicationEmails'],
  })

  const { allCollaborators } = useApplicationCollaborators({ salesforceCreditId: appId, skip: !isShowing })

  const schema = useInviteCollaboratorsSchema({ collaborators: allCollaborators })

  type Schema = yup.InferType<typeof schema>

  const formMethods = useForm<Schema>({
    mode: 'all',
    defaultValues: {
      emails: '',
    },
    resolver: yupResolver(schema),
  })

  const { handleSubmit, control, reset } = formMethods
  const { isDirty, isValid } = useFormState({ control })

  const handleInviteSubmit = useCallback(
    async (data: Schema) => {
      const emailEntries = data.emails.split(',').map((email) => email.trim())

      await inviteUsers({
        variables: {
          creditApplicationId: appId,
          emails: emailEntries,
        },
        onCompleted: () => {
          logInfo('inviteCollaborators', {
            attributes: {
              creditAppId: appId,
              numberOfInvites: emailEntries.length,
              result: 'collaborators invited',
            },
            eventName: 'inviteCollaboratorsSuccess',
          })

          onInviteCollaborator?.()
          showToast(
            TOAST_TYPE_SUCCESS,
            <FormattedMessage
              defaultMessage='Invites successfully sent'
              id='sputnik.InviteCollaboratorsDrawer__ixk27n'
            />,
          )
          reset()
        },
        onError: (error) => {
          logError(error, {
            attributes: {
              action: 'inviteCollaborators',
              creditAppId: appId,
              message: error?.message,
              numberOfInvites: emailEntries.length,
              result: 'failed to invite collaborators',
            },
            eventName: 'inviteCollaboratorsFailure',
          })
          showToast(
            TOAST_TYPE_SUCCESS,
            <FormattedMessage
              defaultMessage='Failed to invite collaborators'
              id='sputnik.InviteCollaboratorsDrawer__011Ad8'
            />,
          )
        },
      })
    },
    [inviteUsers, appId, onInviteCollaborator, showToast, reset],
  )

  return (
    <DrawerView
      footerActions={
        <DrawerActions
          cancelText={
            <FormattedMessage
              defaultMessage='Cancel'
              id='sputnik.LegacyInviteCollaboratorDrawerView__47FYwb'
            />
          }
          confirmButtonColor='action'
          confirmText={
            <FormattedMessage
              defaultMessage='Invite'
              id='sputnik.LegacyInviteCollaboratorDrawerView__hYOE+U'
            />
          }
          inactive={!isValid || !isDirty}
          onCancel={onClose}
          onConfirm={handleSubmit(handleInviteSubmit)}
        />
      }
      title={
        <FormattedMessage
          defaultMessage='Invite collaborators'
          id='sputnik.LegacyInviteCollaboratorDrawerView__ni18Y2'
        />
      }
      {...(icon === 'back'
        ? {
            onBackClick: onClose,
          }
        : {
            onCloseClick: onClose,
          })}
    >
      <FormProvider {...formMethods}>
        <form
          className={`${styles[getClassName('form')]} dd-privacy-allow`}
          onSubmit={handleSubmit(handleInviteSubmit)}
        >
          <div className={styles[getClassName('subheading')]}>
            <FormattedMessage
              defaultMessage='Invite a collaborator to help you fill out this application.'
              id='sputnik.LegacyInviteCollaboratorDrawerView__zYQ/Sf'
            />
          </div>
          <FormTextInput
            caption={
              <FormattedMessage
                defaultMessage='Separate multiple emails with commas'
                id='sputnik.LegacyInviteCollaboratorDrawerView__r43jfV'
              />
            }
            className='dd-privacy-mask'
            control={control}
            label={
              <FormattedMessage
                defaultMessage='Enter collaborator email'
                id='sputnik.LegacyInviteCollaboratorDrawerView__t8gSjR'
              />
            }
            name='emails'
          />
          {allCollaborators.length > 0 && (
            <div className={styles[getClassName('collaborators')]}>
              <div className={styles[getClassName('collaborators-title')]}>
                <FormattedMessage
                  defaultMessage='Current collaborators'
                  id='sputnik.LegacyInviteCollaboratorDrawerView__iSeTDg'
                />
              </div>
              {allCollaborators.map(({ email, firstName, lastName, role }) => (
                <span
                  key={email}
                  className='dd-privacy-mask'
                >
                  <Collaborator
                    key={email}
                    appId={appId}
                    email={email}
                    firstName={firstName}
                    lastName={lastName}
                    role={role}
                    onDeleteCollaborator={onDeleteCollaborator}
                  />
                </span>
              ))}
              <div className={styles[getClassName('collaborators-delete-info')]}>
                <FormattedMessage
                  defaultMessage='If you remove a collaborator, they’ll no longer have access to this application. But the information they entered will be saved.'
                  id='sputnik.LegacyInviteCollaboratorDrawerView__On/dYy'
                />
              </div>
            </div>
          )}
        </form>
      </FormProvider>
    </DrawerView>
  )
}

export default LegacyInviteCollaboratorsDrawerView
